import React, { useState, useRef, useEffect, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { api } from '../utils/api'; 
import { Send, Clock, AlertCircle, Loader2, CheckCircle, List, Globe } from 'lucide-react';

// --- [START] Translations Object ---
const translations = {
  th: {
    langToggle: "EN",
    title: "ยื่นใบขออนุญาตมาสาย",
    subtitle: "กรอกแบบฟอร์มเพื่อส่งคำขออนุญาตมาสาย (กรณีมาสาย)",
    formTitle: "กรอกรายละเอียดการมาสาย",
    dateLabel: "วันที่มาสาย",
    reasonLabel: "เหตุผลที่มาสาย",
    reasonPlaceholder: "กรุณาระบุเหตุผลที่มาสาย...",
    signatureLabel: "ลงลายมือชื่อ (กรุณาลงลายมือชื่อบริเวณตรงกลาง)",
    signatureClear: "ล้างลายเซ็น",
    submitButtonText: "ยื่นใบขออนุญาต (รอพิจารณา)",
    submitButtonSigning: "กรุณาลงลายมือชื่อก่อนส่ง",
    submitButtonSubmitting: "กำลังส่งข้อมูล...",
    listTitle: "ประวัติคำร้องของฉัน",
    listLoading: "กำลังโหลดประวัติ...",
    listError: "ไม่สามารถดึงประวัติคำร้องได้:",
    listEmpty: "ไม่พบประวัติคำร้อง",
    listHeaderDate: "วันที่มาสาย",
    listHeaderReason: "เหตุผล",
    listHeaderStatus: "สถานะ",
    listStatusPending: "รอพิจารณา",
    listStatusApproved: "อนุมัติแล้ว",
    listStatusRejected: "ไม่อนุมัติ",
    errorSignature: "กรุณาลงลายมือชื่อผู้ยื่น",
    errorFormIncomplete: "กรุณากรอกข้อมูลให้ครบถ้วน: วันที่ และ เหตุผลที่มาสาย",
    errorSubmit: "ไม่สามารถยื่นใบคำร้องได้",
    errorConnection: "เกิดข้อผิดพลาดในการเชื่อมต่อ:",
    successSubmit: "ยื่นใบขออนุญาตมาสายสำเร็จ เรื่องของท่านจะถูกส่งให้ผู้ดูแลระบบพิจารณา",
  },
  en: {
    langToggle: "TH",
    title: "Late Arrival Request",
    subtitle: "Fill out the form to request approval for late arrival.",
    formTitle: "Late Arrival Details",
    dateLabel: "Date of Late Arrival",
    reasonLabel: "Reason for Lateness",
    reasonPlaceholder: "Please provide a reason for your late arrival...",
    signatureLabel: "Signature (Please sign in the center)",
    signatureClear: "Clear Signature",
    submitButtonText: "Submit Request (Pending)",
    submitButtonSigning: "Please Sign Before Submitting",
    submitButtonSubmitting: "Submitting...",
    listTitle: "My Request History",
    listLoading: "Loading history...",
    listError: "Could not fetch request history:",
    listEmpty: "No request history found.",
    listHeaderDate: "Date",
    listHeaderReason: "Reason",
    listHeaderStatus: "Status",
    listStatusPending: "Pending",
    listStatusApproved: "Approved",
    listStatusRejected: "Rejected",
    errorSignature: "Please provide your signature.",
    errorFormIncomplete: "Please complete all required fields: Date and Reason.",
    errorSubmit: "Failed to submit request.",
    errorConnection: "Connection error:",
    successSubmit: "Late arrival request submitted successfully! It will be sent for review.",
  }
};

const formatDateThai = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const options = { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Bangkok' };
  return date.toLocaleDateString('th-TH', options);
};

const formatDateEng = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const options = { day: 'numeric', month: 'long', year: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'approved': return 'bg-green-100 text-green-800';
    case 'rejected': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const LateRequestForm = ({ currentUser }) => {
  const [lang, setLang] = useState('th');
  const t = translations[lang];

  const [requestDate, setRequestDate] = useState(new Date().toISOString().split('T')[0]);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const sigPad = useRef(null);
  const [signatureEmpty, setSignatureEmpty] = useState(true);

  const [myRequests, setMyRequests] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState('');

  const fetchMyRequests = useCallback(async () => {
    if (!currentUser || !currentUser.employee_id) return;
    setLoadingList(true);
    setListError('');
    try {
      const data = await api.getLateRequests(currentUser.employee_id);
      setMyRequests(data);
    } catch (err) {
      console.error("Error fetching my late requests:", err);
      setListError(t.listError + err.message);
    } finally {
      setLoadingList(false);
    }
  }, [currentUser, t.listError]); 

  useEffect(() => {
    fetchMyRequests();
  }, [fetchMyRequests]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (sigPad.current.isEmpty()) {
      setSignatureEmpty(true);
      setMessage({ type: 'error', text: t.errorSignature });
      return;
    }
    setSignatureEmpty(false);

    if (!requestDate || !reason.trim()) {
      setMessage({ type: 'error', text: t.errorFormIncomplete });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const signatureDataUrl = sigPad.current.toDataURL('image/png');

      const requestData = {
        employee_id: currentUser.employee_id,
        request_date: requestDate,
        reason: reason,
        applicant_signature: signatureDataUrl,
      };

      const response = await api.submitLateRequest(requestData);

      if (response.success) {
        setMessage({ type: 'success', text: t.successSubmit });
        setRequestDate(new Date().toISOString().split('T')[0]);
        setReason('');
        sigPad.current.clear();
        setSignatureEmpty(true);
        fetchMyRequests(); 
      } else {
        setMessage({ type: 'error', text: response.error || t.errorSubmit });
      }
    } catch (error) {
      console.error("Error submitting late request:", error);
      setMessage({ type: 'error', text: t.errorConnection + error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLanguage = () => {
    setLang(lang === 'th' ? 'en' : 'th');
    setMessage({ type: '', text: '' }); 
  };

  const formatDateByLang = (dateString) => {
    return lang === 'th' ? formatDateThai(dateString) : formatDateEng(dateString);
  };

  return (
    <div className="space-y-8">
      
      {/* (1) Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t.title}</h1>
          <p className="text-gray-600 mt-2">{t.subtitle}</p>
        </div>
        <button
          onClick={toggleLanguage}
          className="flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          <Globe className="h-5 w-5 mr-2 text-gray-500" />
          {t.langToggle}
        </button>
      </div>

      {/* (2) ฟอร์ม */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Clock className="h-6 w-6 mr-2 text-yellow-500" />
            {t.formTitle}
          </h2>
        </div>
        
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t.dateLabel} *</label>
              <input
                type="date"
                value={requestDate}
                onChange={(e) => setRequestDate(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t.reasonLabel} *</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t.reasonPlaceholder}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.signatureLabel} *
              </label>
              <div className={`w-full border rounded-md overflow-hidden bg-gray-50 ${signatureEmpty && message.text.includes(t.errorSignature) ? 'border-red-500' : 'border-gray-300'}`}>
                <SignatureCanvas
                  ref={sigPad}
                  penColor='black'
                  backgroundColor='white'
                  canvasProps={{ className: 'w-full h-32 cursor-crosshair rounded-md' }}
                  onEnd={() => setSignatureEmpty(sigPad.current.isEmpty())}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  sigPad.current.clear();
                  setSignatureEmpty(true);
                }}
                className="mt-2 text-sm text-red-600 hover:text-red-800 transition-colors"
              >
                {t.signatureClear}
              </button>
            </div>

            {message.text && (
              <div className={`p-4 rounded-md flex items-center ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {message.type === 'success' ? <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" /> : <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />}
                <span className="text-sm">{message.text}</span>
              </div>
            )}

            <div className="border-t pt-6">
              <button
                type="submit"
                disabled={submitting || signatureEmpty}
                className={`w-full py-3 px-6 rounded-md flex items-center justify-center space-x-2 text-lg font-medium transition-colors shadow-sm
                  ${(submitting || signatureEmpty) ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}
                `}
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5" />
                    <span>{t.submitButtonSubmitting}</span>
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    <span>{signatureEmpty ? t.submitButtonSigning : t.submitButtonText}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* --- ประวัติคำร้อง --- */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <List className="h-6 w-6 mr-2 text-blue-500" />
            {t.listTitle}
          </h2>
        </div>
        
        <div className="p-6">
          {loadingList && <div className="flex justify-center items-center h-40"><Loader2 className="h-10 w-10 text-blue-500 animate-spin" /><span className="ml-4 text-gray-600">{t.listLoading}</span></div>}
          {listError && <div className="p-4 rounded-md bg-red-50 text-red-700 flex items-center"><AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" /><span>{listError}</span></div>}

          {!loadingList && !listError && (
            <div className="space-y-4">
              {myRequests.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <p className="text-gray-500">{t.listEmpty}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t.listHeaderDate}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t.listHeaderReason}</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t.listHeaderStatus}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {myRequests.map(req => (
                        <tr key={req.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatDateByLang(req.request_date)}</td>
                          <td className="px-6 py-4 text-sm text-gray-700 max-w-sm truncate" title={req.reason}>{req.reason}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(req.status)}`}>
                              {req.status === 'pending' ? t.listStatusPending : req.status === 'approved' ? t.listStatusApproved : t.listStatusRejected}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default LateRequestForm;